class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, #mf-bn-cart-btn, [data-open-cart-drawer]');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // GA4 & Meta Pixel View Cart Tracking
    this.trackViewCart();

    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (typeof window.mfInitCartDiscount === 'function') window.mfInitCartDiscount();
      if (typeof window.mfInitCartNote === 'function') window.mfInitCartNote();
      if (typeof window.mfInitCartDots === 'function') window.mfInitCartDots();
      if (typeof window.mfAnimateProgressBar === 'function') window.mfAnimateProgressBar();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? (this.querySelector('.mf-empty-cart') || this.querySelector('.drawer__inner-empty') || document.getElementById('CartDrawer'))
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn || document.getElementById('CartDrawer'), focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  trackViewCart() {
    try {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cartData) => {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'view_cart',
              ecommerce: {
                currency: cartData.currency || 'INR',
                value: cartData.total_price / 100,
                items: (cartData.items || []).map((item, index) => ({
                  item_id: item.id,
                  item_name: item.title,
                  price: item.price / 100,
                  quantity: item.quantity,
                  index: index
                }))
              }
            });
          }
          if (typeof fbq === 'function') {
            fbq('track', 'ViewContent', {
              content_type: 'product_group',
              value: cartData.total_price / 100,
              currency: cartData.currency || 'INR'
            });
          }
        })
        .catch(() => {});
    } catch (e) {}
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    var inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !parsedState.sections[section.id]) return;
        const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (html) sectionElement.innerHTML = html;
      });
    }

    setTimeout(() => {
      var overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      if (typeof window.mfInitCartDots === 'function') window.mfInitCartDots();
      if (typeof window.mfInitCartDiscount === 'function') window.mfInitCartDiscount();
      if (typeof window.mfInitCartNote === 'function') window.mfInitCartNote();
      if (typeof window.mfAnimateProgressBar === 'function') window.mfAnimateProgressBar();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const el = parsed.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

const BaseCartDrawerItems = typeof CartItems !== 'undefined' ? CartItems : HTMLElement;

class CartDrawerItems extends BaseCartDrawerItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateLiveRegions(line, message) {
    if (typeof super.updateLiveRegions === 'function') {
      super.updateLiveRegions(line, message);
    }
    if (message && typeof window.mfShowCartError === 'function') {
      window.mfShowCartError(message);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ==========================================================================
   Modern Cart Drawer Helpers & Event Delegation
   ========================================================================== */

/** Show error banner toast in drawer */
window.mfShowCartError = function (message) {
  const banner = document.getElementById('CartDrawer-ErrorBanner');
  const text = document.getElementById('CartDrawer-ErrorText');
  if (banner && text) {
    text.textContent = message || 'Something went wrong. Please try again.';
    banner.classList.add('is-visible');
    setTimeout(() => {
      banner.classList.remove('is-visible');
    }, 5000);
  }
};

/** Animate 2-tier milestone shipping & gift progress bar */
window.mfAnimateProgressBar = function () {
  requestAnimationFrame(() => {
    const fill = document.querySelector('.mf-ship-bar__fill');
    if (fill) {
      const targetPct = Math.min(100, Math.max(0, parseFloat(fill.getAttribute('data-progress') || 0)));
      fill.style.width = targetPct + '%';
    }
  });
};

/** Initialize discount coupon state from localStorage */
window.mfInitCartDiscount = function () {
  const code = localStorage.getItem('mf_cart_discount');
  const inputWrapper = document.getElementById('CartDrawer-DiscountWrapper');
  const appliedWrapper = document.getElementById('CartDrawer-DiscountApplied');
  const codeNameEl = document.getElementById('CartDrawer-DiscountCodeName');

  if (code && appliedWrapper && inputWrapper && codeNameEl) {
    codeNameEl.textContent = code;
    inputWrapper.style.display = 'none';
    appliedWrapper.style.display = 'flex';
  } else if (inputWrapper && appliedWrapper) {
    inputWrapper.style.display = 'flex';
    appliedWrapper.style.display = 'none';
  }
};

/** Apply a discount code */
window.mfApplyCoupon = async function (code) {
  if (!code || !code.trim()) return;
  const cleanCode = code.trim().toUpperCase();
  const btn = document.getElementById('CartDrawer-DiscountApply');
  const msgEl = document.getElementById('CartDrawer-DiscountMsg');

  if (btn) btn.disabled = true;

  try {
    localStorage.setItem('mf_cart_discount', cleanCode);
    // Ping Shopify discount endpoint in background
    fetch('/discount/' + encodeURIComponent(cleanCode)).catch(() => {});

    if (msgEl) {
      msgEl.textContent = `Coupon "${cleanCode}" applied!`;
      msgEl.className = 'mf-cart-discount-msg mf-cart-discount-msg--success';
      msgEl.style.display = 'block';
    }

    window.mfInitCartDiscount();
  } catch (err) {
    if (msgEl) {
      msgEl.textContent = 'Could not apply coupon. Please try again.';
      msgEl.className = 'mf-cart-discount-msg mf-cart-discount-msg--error';
      msgEl.style.display = 'block';
    }
  } finally {
    if (btn) btn.disabled = false;
  }
};

/** Remove active discount code */
window.mfRemoveCoupon = function () {
  localStorage.removeItem('mf_cart_discount');
  const input = document.getElementById('CartDrawer-DiscountInput');
  if (input) input.value = '';
  const msgEl = document.getElementById('CartDrawer-DiscountMsg');
  if (msgEl) msgEl.style.display = 'none';
  window.mfInitCartDiscount();
};

/** Debounced auto-save for Cart Order Note */
let mfNoteDebounceTimer;
window.mfInitCartNote = function () {
  const textarea = document.getElementById('CartDrawer-Note');
  const savedIndicator = document.getElementById('CartDrawer-NoteSaved');
  if (!textarea || textarea.dataset.initialized === 'true') return;

  textarea.dataset.initialized = 'true';
  textarea.addEventListener('input', () => {
    if (savedIndicator) savedIndicator.style.display = 'none';
    clearTimeout(mfNoteDebounceTimer);
    mfNoteDebounceTimer = setTimeout(async () => {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: textarea.value }),
        });
        if (savedIndicator) {
          savedIndicator.style.display = 'inline-block';
          setTimeout(() => {
            savedIndicator.style.display = 'none';
          }, 3000);
        }
      } catch (e) {
        console.error('Note auto-save failed:', e);
      }
    }, 600);
  });
};

/** Empty Cart Bestsellers carousel dots */
window.mfInitCartDots = function () {
  const grid = document.getElementById('mf-ec-grid');
  const dotsContainer = document.getElementById('mf-ec-dots');
  if (!grid || !dotsContainer) return;

  const dots = dotsContainer.querySelectorAll('.mf-ec-dot');
  if (!dots.length) return;

  grid.addEventListener(
    'scroll',
    () => {
      const scrollLeft = grid.scrollLeft;
      const cardWidth = grid.querySelector('.mf-empty-cart__product')?.offsetWidth || 150;
      const activeIndex = Math.min(dots.length - 1, Math.round(scrollLeft / cardWidth));

      dots.forEach((dot, idx) => {
        dot.classList.toggle('active', idx === activeIndex);
      });
    },
    { passive: true }
  );
};

/** Utility: Refresh Drawer Contents using Section Rendering */
async function mfRefreshCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  try {
    const res = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`);
    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Update inner drawer
    const newInner = doc.querySelector('.drawer__inner');
    const curInner = document.querySelector('.drawer__inner');
    if (newInner && curInner) {
      curInner.innerHTML = newInner.innerHTML;
    }

    // Update cart bubble count
    const bubbleRes = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-icon-bubble`);
    const bubbleHtml = await bubbleRes.text();
    const bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
    const newBubble = bubbleDoc.querySelector('.shopify-section');
    const curBubble = document.querySelector('#cart-icon-bubble');
    if (newBubble && curBubble) {
      curBubble.innerHTML = newBubble.innerHTML;
    }

    // Update cart items count & drawer empty state
    const cartJsonRes = await fetch('/cart.js');
    const cartData = await cartJsonRes.json();
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', cartData.item_count === 0);
    }

    // Mobile nav badge sync
    const mobCount = document.querySelector('.mf-bottom-nav__badge, #mf-bn-cart-count');
    if (mobCount) {
      mobCount.textContent = cartData.item_count;
      mobCount.style.display = cartData.item_count > 0 ? 'inline-flex' : 'none';
    }

    // Re-initialize dynamic widgets
    if (typeof window.mfAnimateProgressBar === 'function') window.mfAnimateProgressBar();
    if (typeof window.mfInitCartDiscount === 'function') window.mfInitCartDiscount();
    if (typeof window.mfInitCartNote === 'function') window.mfInitCartNote();
    if (typeof window.mfInitCartDots === 'function') window.mfInitCartDots();
  } catch (err) {
    console.error('Cart drawer refresh failed:', err);
  }
}

/** Global Delegated Event Handlers */
document.addEventListener('DOMContentLoaded', () => {
  window.mfInitCartDiscount();
  window.mfInitCartNote();
  window.mfInitCartDots();
  window.mfAnimateProgressBar();
});

document.addEventListener('click', async (e) => {
  // 1. Preset Coupon Apply Buttons
  const presetBtn = e.target.closest('.mf-offer-apply-btn');
  if (presetBtn) {
    const card = presetBtn.closest('.mf-offer-ticket');
    const codeBadge = card?.querySelector('.mf-offer-code-badge');
    const code = codeBadge ? codeBadge.textContent.trim() : '';
    if (code) window.mfApplyCoupon(code);
    return;
  }

  // 2. Manual Coupon Apply Button
  if (e.target.closest('#CartDrawer-DiscountApply')) {
    const input = document.getElementById('CartDrawer-DiscountInput');
    if (input && input.value.trim()) {
      window.mfApplyCoupon(input.value.trim());
    }
    return;
  }

  // 3. Remove Applied Coupon
  if (e.target.closest('#CartDrawer-DiscountRemove')) {
    window.mfRemoveCoupon();
    return;
  }

  // 4. Clear All Items in Cart
  if (e.target.closest('#CartDrawer-ClearAll')) {
    const clearBtn = e.target.closest('#CartDrawer-ClearAll');
    clearBtn.disabled = true;
    clearBtn.textContent = 'Clearing...';
    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      await mfRefreshCartDrawer();
    } catch (err) {
      window.mfShowCartError('Failed to clear cart. Please try again.');
    } finally {
      clearBtn.disabled = false;
      clearBtn.textContent = 'Clear all';
    }
    return;
  }

  // 5. Upsell Quantity Stepper (Minus)
  const upsellMinus = e.target.closest('[data-upsell-minus]');
  if (upsellMinus) {
    const card = upsellMinus.closest('.mf-cart-upsell__card');
    const input = card?.querySelector('[data-upsell-qty]');
    if (input) {
      const current = parseInt(input.value) || 1;
      if (current > 1) input.value = current - 1;
    }
    return;
  }

  // 6. Upsell Quantity Stepper (Plus)
  const upsellPlus = e.target.closest('[data-upsell-plus]');
  if (upsellPlus) {
    const card = upsellPlus.closest('.mf-cart-upsell__card');
    const input = card?.querySelector('[data-upsell-qty]');
    if (input) {
      const current = parseInt(input.value) || 1;
      if (current < 99) input.value = current + 1;
    }
    return;
  }

  // 7. Upsell Add to Cart
  const upsellAtc = e.target.closest('.mf-cart-upsell__atc');
  if (upsellAtc && !upsellAtc.disabled) {
    const card = upsellAtc.closest('.mf-cart-upsell__card');
    const variantSelect = card?.querySelector('[data-upsell-variant]');
    const variantId = variantSelect ? variantSelect.value : upsellAtc.getAttribute('data-variant-id');
    const qtyInput = card?.querySelector('[data-upsell-qty]');
    const qty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

    if (!variantId) return;

    const originalText = upsellAtc.innerHTML;
    upsellAtc.disabled = true;
    upsellAtc.innerHTML = '<span class="mf-btn-spinner"></span> Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.description || 'Failed to add item');
      }
      await mfRefreshCartDrawer();
    } catch (err) {
      window.mfShowCartError(err.message || 'Could not add item to cart');
      upsellAtc.disabled = false;
      upsellAtc.innerHTML = originalText;
    }
    return;
  }

  // 8. Empty Cart Quantity Stepper (Minus)
  const ecMinus = e.target.closest('[data-ec-minus]');
  if (ecMinus) {
    const card = ecMinus.closest('.mf-empty-cart__product');
    const qtyEl = card?.querySelector('[data-ec-qty]');
    if (qtyEl) {
      const current = parseInt(qtyEl.textContent) || 1;
      if (current > 1) qtyEl.textContent = current - 1;
    }
    return;
  }

  // 9. Empty Cart Quantity Stepper (Plus)
  const ecPlus = e.target.closest('[data-ec-plus]');
  if (ecPlus) {
    const card = ecPlus.closest('.mf-empty-cart__product');
    const qtyEl = card?.querySelector('[data-ec-qty]');
    if (qtyEl) {
      const current = parseInt(qtyEl.textContent) || 1;
      if (current < 99) qtyEl.textContent = current + 1;
    }
    return;
  }

  // 10. Empty Cart Add to Cart
  const ecAtc = e.target.closest('.mf-empty-cart__atc');
  if (ecAtc && !ecAtc.disabled) {
    const card = ecAtc.closest('.mf-empty-cart__product');
    const variantSelect = card?.querySelector('[data-ec-variant-select]');
    const variantId = variantSelect ? variantSelect.value : ecAtc.getAttribute('data-variant-id');
    const qtyEl = card?.querySelector('[data-ec-qty]');
    const qty = qtyEl ? parseInt(qtyEl.textContent) || 1 : 1;

    if (!variantId) return;

    const originalText = ecAtc.innerHTML;
    ecAtc.disabled = true;
    ecAtc.innerHTML = '<span class="mf-btn-spinner"></span> Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.description || 'Failed to add item');
      }
      await mfRefreshCartDrawer();
    } catch (err) {
      window.mfShowCartError(err.message || 'Could not add item to cart');
      ecAtc.disabled = false;
      ecAtc.innerHTML = originalText;
    }
    return;
  }
});

// Upsell and Empty Cart Variant Select changes
document.addEventListener('change', (e) => {
  // Upsell Variant dropdown change
  if (e.target.matches('[data-upsell-variant]')) {
    const select = e.target;
    const card = select.closest('.mf-cart-upsell__card');
    const selectedOption = select.options[select.selectedIndex];
    const atcBtn = card?.querySelector('.mf-cart-upsell__atc');
    const priceEl = card?.querySelector('.mf-cart-upsell__price');

    if (atcBtn) {
      atcBtn.setAttribute('data-variant-id', select.value);
      if (selectedOption.disabled) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Sold out';
      } else {
        atcBtn.disabled = false;
        atcBtn.textContent = 'ADD TO CART';
      }
    }
    if (priceEl && selectedOption.dataset.price) {
      priceEl.textContent = selectedOption.dataset.price;
    }
    return;
  }

  // Empty cart Variant dropdown change
  if (e.target.matches('[data-ec-variant-select]')) {
    const select = e.target;
    const card = select.closest('.mf-empty-cart__product');
    const opt = select.options[select.selectedIndex];
    const atcBtn = card?.querySelector('.mf-empty-cart__atc');
    const priceEl = card?.querySelector('.mf-empty-cart__product-price');
    const compareEl = card?.querySelector('.mf-empty-cart__compare-price');
    const saveEl = card?.querySelector('.mf-empty-cart__save-text');

    if (atcBtn) {
      atcBtn.setAttribute('data-variant-id', select.value);
      if (opt.disabled) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Sold out';
      } else {
        atcBtn.disabled = false;
        atcBtn.textContent = 'ADD TO CART';
      }
    }
    if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    if (compareEl) {
      if (opt.dataset.hasCompare === 'true') {
        compareEl.textContent = opt.dataset.compare;
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }
    if (saveEl) {
      if (opt.dataset.hasCompare === 'true' && opt.dataset.saving) {
        saveEl.textContent = `Save ${opt.dataset.saving}`;
        saveEl.style.display = 'block';
      } else {
        saveEl.style.display = 'none';
      }
    }
  }
});

// Discount input Enter key handler
document.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && e.target.id === 'CartDrawer-DiscountInput') {
    e.preventDefault();
    if (e.target.value.trim()) {
      window.mfApplyCoupon(e.target.value.trim());
    }
  }
});
